import { useContext } from 'react';
import { Context as SettingsContext } from 'components/setting/context';

/**
 * The restricted master switch. ePublishing decides whether a site may run any
 * of this at all, and every feature switch below is meaningless without it.
 *
 * `restricted` keeps a Global Variable out of the Settings page for anyone
 * without the access.restricted.system.settings privilege; it is not filtered
 * by the API, so this reads fine. A site still cannot switch itself on -- it
 * cannot edit what it cannot see -- which is the point of the flag.
 */
const MASTER_SETTING = 'ai.enabled';

/**
 * The AI content lifecycle features a site can switch on, and the client-facing
 * Global Variable behind each (CMS-7287).
 *
 * `assistant` is the editorial AI drawer; `vector` is the embedding index that
 * powers semantic search and the AI Related panel; `lifecycle` is the staleness
 * evaluator, its admin page and the flags it writes onto articles.
 */
export const FEATURE_SETTINGS = {
  assistant: 'ai.assistant.enabled',
  vector: 'ai.vector.enabled',
  lifecycle: 'ai.lifecycle.enabled',
} as const;

export type AIFeature = keyof typeof FEATURE_SETTINGS;

/**
 * Whether an AI feature is on for this site.
 *
 * Mirrors Epublishing::Ai.feature_enabled? in jade exactly: the restricted
 * ai.enabled master ANDed with the feature's own switch. Both halves matter.
 * A site with ai.vector.enabled true but no ai.enabled row -- which is every
 * site that has not been seeded since the master switch landed -- has vector
 * search switched OFF server-side, and the UI has to agree or it offers tabs
 * and panels the API will refuse.
 *
 * Unset reads as off, for either half. These features bill per call at Amazon
 * Bedrock, so the gate fails closed: a site that cannot be asked shows nothing.
 */
export function useAIFeature(feature: AIFeature): boolean {
  const { settings } = useContext(SettingsContext);

  const masterOn = settings[MASTER_SETTING]?.stringValue === 'true';
  const featureOn = settings[FEATURE_SETTINGS[feature]]?.stringValue === 'true';

  return masterOn && featureOn;
}

export default useAIFeature;
